/**
 * Submit/autosave race orchestrator (bug #1 fix).
 *
 * Extracted from StepForm so the ordering invariants can be unit-tested with
 * injected fake deps and manually-controlled promises — no jsdom, no React,
 * no IndexedDB needed.
 *
 * Invariants this module enforces:
 *  1. Once a submit starts, every draft producer (engine autosave, showcase
 *     debounce) is permanently frozen — even a late debounced autosave that
 *     fires after the submit completes must not write a stale draft back.
 *  2. portal.submitStep is never called while an in-flight server draft POST
 *     is still pending — overlapping draft saves (engine + showcase fire
 *     independently) are all awaited before the submit request goes out.
 *  3. onAutoSave carries a SECOND gate check after the local IndexedDB write:
 *     a submit can start between gate 1 and the async IDB call; the
 *     just-written draft is then immediately cleared and the server POST
 *     is skipped.
 */

export interface SaveOrchestratorDeps {
  /** Write a draft to the server (portal.saveDraft for this appId+stepKey). */
  saveDraft(data: Record<string, unknown>): Promise<unknown>;
  /** Write a draft to local storage / IndexedDB (drafts.save). */
  saveLocalDraft(data: Record<string, unknown>): Promise<void>;
  /** Clear the local draft (drafts.clear). */
  clearLocalDraft(): Promise<void>;
}

export interface SaveOrchestrator {
  /**
   * Mutable flag: true while a submit is in progress or has succeeded.
   * Exposed so the component can check it inline and reset it on failure.
   */
  submittingRef: { current: boolean };
  /**
   * Register an in-flight server draft promise in the pending set.
   * The promise removes itself when it settles (resolve or reject).
   * Returns the same promise so callers can chain `.then()` on it.
   */
  trackServerDraft(p: Promise<unknown>): Promise<unknown>;
  /** Set submittingRef = true (called at the top of save()). */
  freeze(): void;
  /** Reset submittingRef = false (called in save()'s failure handler). */
  unfreeze(): void;
  /**
   * Await every pending server-draft POST before the submit fires.
   * Uses a while loop so drafts that arrive between settlement rounds
   * (impossible once freeze() is set, but defensive) are also drained.
   */
  drainPendingDrafts(): Promise<void>;
  /**
   * Engine autosave callback — wire directly to FormEnginePortable.onAutoSave.
   *
   * Gate 1  → bail immediately if submitting (no IDB write, no server POST).
   * IDB     → write local draft (fast, survives offline reloads).
   * Gate 2  → if submit started during the IDB await: clear the just-written
   *            draft and bail (the submit's own drafts.clear races otherwise).
   * Server  → tracked server draft POST.
   */
  onAutoSave(data: Record<string, unknown>): Promise<void>;
  /**
   * Showcase debounced server save core — the component wrapper adds the
   * scSaveSeq / setScSaveStatus UI state around this call.
   *
   * Returns undefined (and does nothing) when frozen; returns the tracked
   * promise when the POST is issued, so the caller can chain status updates.
   */
  scServerSave(data: Record<string, unknown>): Promise<unknown> | undefined;
}

export function createSaveOrchestrator(deps: SaveOrchestratorDeps): SaveOrchestrator {
  const submittingRef = { current: false };
  const pendingServerDrafts = new Set<Promise<unknown>>();

  function trackServerDraft(p: Promise<unknown>): Promise<unknown> {
    pendingServerDrafts.add(p);
    p.catch(() => {}).finally(() => pendingServerDrafts.delete(p));
    return p;
  }

  function freeze(): void {
    submittingRef.current = true;
  }

  function unfreeze(): void {
    submittingRef.current = false;
  }

  async function drainPendingDrafts(): Promise<void> {
    while (pendingServerDrafts.size > 0) {
      await Promise.allSettled([...pendingServerDrafts]);
    }
  }

  async function onAutoSave(data: Record<string, unknown>): Promise<void> {
    // Gate 1: a submit in progress (or already completed) owns the data —
    // skip the write entirely (bug #1).
    if (submittingRef.current) return;
    // Local first (instant, survives offline reloads).
    await deps.saveLocalDraft(data);
    // Gate 2: submit started while the IDB write was in flight — drop the
    // just-written draft so the submit's own clear() wins (bug #1, mid-write
    // race).
    if (submittingRef.current) {
      await deps.clearLocalDraft().catch(() => {});
      return;
    }
    // Durable: Globus draft snapshot (no validation, no completion). Tracked
    // so save() drains it before portal.submitStep fires.
    await trackServerDraft(deps.saveDraft(data));
  }

  function scServerSave(data: Record<string, unknown>): Promise<unknown> | undefined {
    if (submittingRef.current) return undefined;
    return trackServerDraft(deps.saveDraft(data));
  }

  return {
    submittingRef,
    trackServerDraft,
    freeze,
    unfreeze,
    drainPendingDrafts,
    onAutoSave,
    scServerSave,
  };
}
